import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Menu,
  Checkbox,
  Text,
  UnstyledButton,
  Group,
  Button,
  Box,
} from "@mantine/core";
import { IconTag, IconX, IconTrash } from "@tabler/icons";
import { OverflownText } from "../OverflownText";

interface TagOption {
  value: string;
  label: string;
  color: string;
}

interface TagMenuProps {
  isReadOnly?: boolean;
  selectedTags: string[];
  onTagToggle: (tagId: string) => void;
  tagsOptions: TagOption[];
  onClose?: () => void;
  colTitle: string;
  showAddBtn?: boolean;
  singleSelect?: boolean;
  columnWidth?: number;
  onClearSelection?: () => void;
}

const TagMenu: React.FC<TagMenuProps> = ({
  isReadOnly = false,
  columnWidth,
  selectedTags,
  onTagToggle,
  tagsOptions,
  onClose,
  colTitle,
  showAddBtn = false,
  singleSelect = false,
  onClearSelection,
}) => {
  const [visibleTags, setVisibleTags] = useState<TagOption[]>([]);
  const [overflowCount, setOverflowCount] = useState<number>(0);
  const targetRef = useRef<HTMLButtonElement>(null);
  const [menuOpened, setMenuOpened] = useState(false);
  const validTags = selectedTags.filter((tag) => tagsOptions.some((t) => t.value === tag));

  useEffect(() => {
    const updateVisibleTags = () => {
      if (!targetRef.current) return;

      // Use columnWidth if available, otherwise use container's offsetWidth
      const containerWidth = columnWidth || targetRef.current.offsetWidth;
      let totalWidth = 0;
      const visible: TagOption[] = [];
      let overflow = 0;

      // For singleSelect, only process the first tag in the array
      const tagsToProcess = singleSelect && selectedTags.length > 0 
        ? [selectedTags[0]] 
        : selectedTags;

      tagsToProcess.forEach((tagId, idx) => {
        const tag = tagsOptions.find((tag) => tag.value === tagId);
        if (tag) {
          // If single tag and columnWidth is available, use it for max width
          const maxTagWidth = tagsToProcess.length === 1 && columnWidth 
            ? columnWidth - 30 
            : 100;
          const tagWidth = Math.min(tag.label.length * 8 + 24, maxTagWidth);
          // Always show at least the first tag, regardless of container width
          if (idx === 0 || totalWidth + tagWidth <= containerWidth - 30) {
            visible.push(tag);
            totalWidth += tagWidth;
          } else {
            overflow += 1;
          }
        }
      });

      setVisibleTags(visible);
      setOverflowCount(overflow);
    };

    updateVisibleTags();
    window.addEventListener("resize", updateVisibleTags);
    return () => window.removeEventListener("resize", updateVisibleTags);
  }, [selectedTags, tagsOptions, columnWidth, singleSelect]);

  // Calculate dropdown width based on content
  const dropdownWidth = useMemo(() => {
    // Calculate width based on longest tag label
    const longestTagLabel = tagsOptions.reduce((max, tag) => {
      return tag.label.length > max ? tag.label.length : max;
    }, colTitle.length);
    
    // Calculate width: tag label width + padding + button area
    const tagWidth = longestTagLabel * 8 + 40; // 8px per character + padding
    const buttonArea = selectedTags.length > 0 ? 240 : 120; // Clear + Close buttons or just Close
    const calculatedWidth = Math.max(tagWidth, buttonArea);
    
    // Clamp between min and max
    return Math.min(Math.max(calculatedWidth, 200), 600);
  }, [tagsOptions, colTitle, selectedTags.length]);

  return (
    <Menu
      disabled={isReadOnly}
      position="bottom-start"
      shadow="sm"
      opened={menuOpened}
      onChange={setMenuOpened}
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          style={{ 
            width: columnWidth ? `${columnWidth}px` : "100%", 
            textAlign: "left",
            maxWidth: columnWidth ? `${columnWidth}px` : "100%"
          }}
          ref={targetRef}
          onClick={() => setMenuOpened((o) => !o)}
        >
          <Group 
            noWrap
            spacing={6}
            sx={{
              "&:hover": {
                backgroundColor: "#F8F8FB",
              },
              cursor: isReadOnly ? "default" : "pointer",
              width: columnWidth && (singleSelect || selectedTags.length === 1) ? "100%" : "min-content",
              textAlign: "left",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            <Group
              spacing={6}
              noWrap
              style={{
                flexWrap: "nowrap",
                overflow: "hidden",
                width: "min-content",
              }}
            >
              {visibleTags.map((tag) => {
                // If singleSelect with one tag and columnWidth is available, use it for max width
                const maxWidth = (singleSelect || visibleTags.length === 1) && columnWidth 
                  ? columnWidth - 30 
                  : 100;
                return (
                  <OverflownText
                    key={tag.value}
                    size="sm"
                    style={{
                      backgroundColor:
                        tag.color === "#585C68" ? "#EFEFFD" : `${tag.color}20`,
                      color: tag.color,
                      padding: "0px 8px",
                      borderRadius: "4px",
                      display: "inline-block",
                      maxWidth: maxWidth,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      border: "1px solid transparent"
                    }}
                  >
                    {tag.label}
                  </OverflownText>
                );
              })}
              {overflowCount > 0 && (
                <Text
                  size="sm"
                  style={{
                    color: "#5C5CEB",
                    marginLeft: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{overflowCount}
                </Text>
              )}
              {showAddBtn && (
                <UnstyledButton
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    padding: "0px 4px",
                    borderRadius: 4,
                    color: "#5B60E3",
                    fontWeight: 500,
                    width: 82,
                  }}
                  onClick={() => setMenuOpened((o) => !o)}
                >
                  <IconTag size={12} strokeWidth={2} color="#5B60E3" />
                  <Text lineClamp={1} size="sm">
                    {singleSelect ? "Add tag" : "Add tags"}
                  </Text>
                </UnstyledButton>
              )}
              {!showAddBtn && validTags?.length === 0 && (
                <Text
                  w="max-content"
                  size="sm"
                  color="#aeaeae"
                  lineClamp={1}
                  px={3}
                  sx={{
                    border:"1px solid transparent",
                    borderRadius: 4
                  }}
                >
                    Select {colTitle.toLowerCase()}
                </Text>
              )}
            </Group>
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown 
        p={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          zIndex: 10000,
          width: `${dropdownWidth}px`,
          maxHeight: "400px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            maxHeight: "320px",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px",
            flex: 1,
          }}
        >
          {tagsOptions.map((tag) => {
            const isSelected = selectedTags.includes(tag.value);
            return (
              <Menu.Item
                key={tag.value}
                p={6}
                m={2}
                onClick={() => onTagToggle(tag.value)}
                closeMenuOnClick={singleSelect}
                sx={{
                  backgroundColor: isSelected && singleSelect ? "#F0F0FF" : "transparent",
                  "&:hover": {
                    backgroundColor: isSelected && singleSelect ? "#E8E8FF" : "#F8F8FB",
                  },
                  width: "100%",
                  display: "block",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {!singleSelect && (
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onTagToggle(tag.value)}
                      size="sm"
                      color="indigo"
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {singleSelect && (
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: `2px solid ${isSelected ? "#5C5CEB" : "#D1D5DB"}`,
                        backgroundColor: isSelected ? "#5C5CEB" : "transparent",
                        marginRight: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "white",
                          }}
                        />
                      )}
                    </div>
                  )}
                  <Text
                    size="sm"
                    style={{
                      backgroundColor:
                        tag.color === "#585C68" ? "#EFEFFD" : `${tag.color}20`,
                      color: tag.color,
                      padding: "0px 4px",
                      borderRadius: "4px",
                      display: "inline-block",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={tag.label}
                  >
                    {tag.label}
                  </Text>
                </div>
              </Menu.Item>
            );
          })}
          {tagsOptions.length === 0 && (
            <Menu.Item
              p={4}
              m={4}
              w={"-webkit-fill-available"}
              closeMenuOnClick={false}
              sx={{
                width: "100%",
                display: "block",
              }}
            >
              <Text
                size="sm"
                color="#5C5CEB"
                component="a"
                href={"/settings/tags"}
                target="_blank"
                style={{
                  color: "#5C5CEB",
                  textDecoration: "none",
                }}
              >
                Create new {colTitle.toLowerCase()} +
              </Text>
            </Menu.Item>
          )}
        </Box>
        <Box
          sx={{
            borderRadius: "0 0 4px 4px",
            textAlignLast: "right",
            background: "#F8F9FA",
            flexShrink: 0,
          }}
          w={"-webkit-fill-available"}
          mt={0}
          p={8}
        >
          <Group spacing={8} position="right">
            <Button
              onClick={() => {
                setMenuOpened(false);
                onClose?.();
              }}
              styles={(theme) => ({
                root: {
                  backgroundColor: "#5B60E3",
                  border: 0,
                  height: 32,
                  fontSize: 12,
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#5B60E3", 0.05),
                  },
                },
                label: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              })}
              leftIcon={<IconX size={15} />}
            >
              Close
            </Button>
          </Group>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default TagMenu;
